import type { ModelType } from "../types/ModelType";

/**
 * The project's models as the save writes them: one `{ name, active }` per row, sorted by name.
 *
 * One projection, three readers — the settings rail's edited marker, the save's own gate, and the
 * trail row's diff — because the invariant between them is that **the rail lights exactly when a save
 * would write something**. Two hand-built projections a few files apart are how that stops being true.
 *
 * The whole list, not the ACTIVE NAMES it used to be. Since RTLCA-5745 naming a model on the settings
 * page is an edit held until Save, and "created, then turned off before saving" leaves an active-name
 * projection identical on both sides: the marker stayed dark and the save skipped the write, so the
 * model the reader created fell on the floor.
 *
 * Sorted, because both sides legitimately arrive in different orders — a sync appends the rows it
 * imported, a wrapper rebuilt from `speckleData` lists the active models first — and an order is not
 * a change anyone made. `replaceProjectModels` deletes and re-inserts the whole set, so it is not a
 * change the write could carry either.
 *
 * A row staged for removal is NOT in it (RTLCA-5756). The contract is what the save will WRITE, and
 * since RTLCA-5745 removing a model is an edit held until Save like naming one: the row stays on the
 * list so the card can show what is leaving, and this one filter is what makes the removal real
 * everywhere at once — the rail lights, `replaceProjectModels` omits the name from the set it
 * re-inserts, and `modelSignatureChanges` reads it as the `to: null` that `modelChangeSummary`
 * already renders as `Model "X" removed`. Reading the flag anywhere else would be a second
 * projection of the same thing, which is exactly what this module exists to prevent.
 */
export type ProjectModelFlag = { name: string; active: boolean };

export const modelSignature = (models: Array<Pick<ModelType, "name" | "isActive" | "pendingRemoval">>): ProjectModelFlag[] =>
  models
    .filter((model) => !model.pendingRemoval)
    .map((model) => ({ name: model.name, active: !!model.isActive }))
    .sort((a, b) => a.name.localeCompare(b.name));

/** The active names, which only the REST UpdateBranch fallback still speaks. */
export const activeNamesOf = (signature: ProjectModelFlag[]): string[] =>
  signature.filter((model) => model.active).map((model) => model.name);

/** A model that arrived or went is `null` on the side it was missing from — the em dash the trail draws. */
export type ProjectModelChange = { field: string; from: string | null; to: string | null };

/** What moved between two signatures, in the shape the version trail renders. */
export const modelSignatureChanges = (models: ProjectModelFlag[], previous: ProjectModelFlag[]): ProjectModelChange[] => {
  const was = new Map(previous.map((model) => [model.name, model.active]));
  const changes: ProjectModelChange[] = [];

  for (const model of models) {
    const before = was.get(model.name);
    const state = model.active ? "on" : "off";
    if (before === undefined) changes.push({ field: model.name, from: null, to: state });
    else if (before !== model.active) changes.push({ field: model.name, from: before ? "on" : "off", to: state });
  }

  const kept = new Set(models.map((model) => model.name));
  for (const model of previous) {
    if (!kept.has(model.name)) changes.push({ field: model.name, from: model.active ? "on" : "off", to: null });
  }

  return changes;
};

/**
 * The one-line summary for a save that moved exactly one model — word for word the three sentences the
 * immediate add and remove writes used to log on their own, so moving the add into the Save does not
 * change how the project's history reads.
 */
export const modelChangeSummary = (change: ProjectModelChange): string => {
  if (change.from === null) return `Model "${change.field}" added`;
  if (change.to === null) return `Model "${change.field}" removed`;
  return `Model "${change.field}" turned ${change.to}`;
};
